import React from 'react';
import PropTypes from 'prop-types';
import {
  EVENT_ICONS,
  ICON_BY_EVENT_TYPE,
  canonicalIconKey
} from '../../trm/smartAssessment/FramlAlertDetailShell/components/eventIcons';

/**
 * Renders a configured `iconKey`.
 *
 * DELIBERATELY THE SPATIAL VIEW'S OWN REGISTRY, not a second one. The whole point
 * of `icon_key` is that the glyph an admin picks here is the glyph drawn on the
 * timeline — two registries would drift, and the drift would only show up on an
 * alert screen, far from the person who chose it.
 *
 * FALLS BACK TO THE TYPE'S DEFAULT GLYPH, the same one the timeline draws.
 * Most types have no `icon_key` set, and resolving those through
 * `getEventIcon(undefined)` gave every row the generic apartment glyph — a list
 * of identical icons, where the design shows Login as an arrow, OTP as a speech
 * bubble, Password Reset as a key. `ICON_BY_EVENT_TYPE` is the map the timeline
 * already uses for exactly this, so reading it here keeps the two surfaces
 * agreeing by construction rather than by discipline.
 *
 * Precedence: the admin's choice, then the type's default, then NOTHING. The
 * last step is deliberate and matches the design — its "Beneficiary Update" row,
 * a type with no default, renders no icon at all rather than a placeholder.
 * `getEventIcon` is not used here for that reason: its apartment fallback is
 * right for a timeline node, which would otherwise be an empty circle, and wrong
 * for a list row, which reads fine without one.
 *
 * THERE IS NO `fallback` ESCAPE HATCH any more. It existed for one caller — the
 * icon picker's "Default" swatch, which had to show the glyph you would get, and
 * an empty swatch would have read as a broken option. That control was removed
 * on 2026-09-03 to match the design, so the prop went with it rather than
 * lingering as an option nothing sets.
 *
 * THE LIBRARY QUESTION IS SETTLED (2026-09-03): the design's picker captions
 * itself "Standard Lucide icon set (lucide.dev)" and offers thirty-six glyphs, so
 * the registry now holds those (plus two legacy-only ones) keyed by lucide's own
 * names. `canonicalIconKey` is what makes the old ad-hoc keys still resolve —
 * `icon_key` is persisted, so the rekey is a data migration, not a rename.
 *
 * The import reaches into the alert-detail folder because that is where the
 * registry lives today; it should move somewhere shared when the set grows.
 */
const EventIcon = ({ iconKey, technicalValue, className }) => {
  const key = iconKey || (technicalValue && ICON_BY_EVENT_TYPE[technicalValue]);
  const Glyph = key ? EVENT_ICONS[canonicalIconKey(key)] : null;
  if (!Glyph) return null;
  return (
    <span className={className} aria-hidden="true">
      <Glyph />
    </span>
  );
};

EventIcon.propTypes = {
  iconKey: PropTypes.string,
  /** The event type's `technical_value`, used to pick its default glyph. */
  technicalValue: PropTypes.string,
  className: PropTypes.string
};

export default EventIcon;
